/**
 * Site Geo-Fencing Service
 * Provides client-side GPS capture, Haversine distance calculation,
 * accuracy evaluation, and backend API integration.
 */

import { projectInstance } from "../api/axiosInstance";

// Earth radius in meters
export const EARTH_RADIUS_METERS = 6371000.0;

// Geofence status constants
export const GEOFENCE_STATUS = {
  WITHIN_SITE: "WITHIN_SITE",
  OUTSIDE_SITE: "OUTSIDE_SITE",
  LOCATION_ACCURACY_TOO_LOW: "LOCATION_ACCURACY_TOO_LOW",
  LOCATION_PERMISSION_DENIED: "LOCATION_PERMISSION_DENIED",
  LOCATION_UNAVAILABLE: "LOCATION_UNAVAILABLE",
  LOCATION_TIMEOUT: "LOCATION_TIMEOUT",
  SITE_GEOFENCE_NOT_CONFIGURED: "SITE_GEOFENCE_NOT_CONFIGURED",
  SITE_GEOFENCE_DISABLED: "SITE_GEOFENCE_DISABLED",
  UNAUTHORIZED_SITE: "UNAUTHORIZED_SITE",
  IDLE: "IDLE",
  LOADING: "LOADING",
};

/**
 * Calculates geographic distance in meters between two lat/lng pairs using the Haversine formula.
 */
export function calculateHaversineDistance(lat1, lon1, lat2, lon2) {
  if (
    lat1 === undefined || lat1 === null ||
    lon1 === undefined || lon1 === null ||
    lat2 === undefined || lat2 === null ||
    lon2 === undefined || lon2 === null
  ) {
    return null;
  }

  const toRad = (deg) => (Number(deg) * Math.PI) / 180;

  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const deltaPhi = toRad(Number(lat2) - Number(lat1));
  const deltaLambda = toRad(Number(lon2) - Number(lon1));

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);

  const clampedA = Math.min(1.0, Math.max(0.0, a));
  const c = 2 * Math.atan2(Math.sqrt(clampedA), Math.sqrt(1 - clampedA));

  return Math.round(EARTH_RADIUS_METERS * c * 100) / 100;
}

/**
 * Safely formats a coordinate (latitude/longitude) to at most maxDecimals (default 7).
 * Ensures coordinates fit within backend DecimalField(max_digits=11, decimal_places=8).
 */
export function formatCoordinate(coord, maxDecimals = 7) {
  if (coord === undefined || coord === null || isNaN(Number(coord))) {
    return coord;
  }
  return Number(Number(coord).toFixed(maxDecimals));
}

/**
 * Captures high-accuracy GPS coordinates via navigator.geolocation.
 */
export function getCurrentGPSLocation(customOptions = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator || !navigator.geolocation) {
      const err = new Error("Geolocation is not supported by your browser or device.");
      err.code = "UNSUPPORTED";
      return reject(err);
    }

    const options = {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 10000,
      ...customOptions,
    };

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy, altitude, heading, speed } = pos.coords;
        resolve({
          latitude: formatCoordinate(latitude, 7),
          longitude: formatCoordinate(longitude, 7),
          accuracy: Number(accuracy),
          altitude,
          heading,
          speed,
          timestamp: pos.timestamp || Date.now(),
        });
      },
      (error) => {
        const customErr = new Error(error.message);
        customErr.code = error.code;
        if (error.code === 1) {
          customErr.status = GEOFENCE_STATUS.LOCATION_PERMISSION_DENIED;
          customErr.userMessage = "Location permission is required to submit.";
        } else if (error.code === 2) {
          customErr.status = GEOFENCE_STATUS.LOCATION_UNAVAILABLE;
          customErr.userMessage = "Unable to determine your location. Please enable location services and try again.";
        } else if (error.code === 3) {
          customErr.status = GEOFENCE_STATUS.LOCATION_TIMEOUT;
          customErr.userMessage = "Unable to determine your location. Please enable location services and try again.";
        } else {
          customErr.status = GEOFENCE_STATUS.LOCATION_UNAVAILABLE;
          customErr.userMessage = "Unable to determine your location. Please enable location services and try again.";
        }
        reject(customErr);
      },
      options
    );
  });
}

/**
 * Evaluates geofence status locally for immediate UI feedback.
 */
export function evaluateLocationStatus({ userLat, userLon, accuracy = 0, siteConfig }) {
  if (!siteConfig) {
    return {
      status: GEOFENCE_STATUS.SITE_GEOFENCE_NOT_CONFIGURED,
      message: "Site location is not configured. Please contact the administrator.",
      distance: null,
      allowed: false,
    };
  }

  if (siteConfig.geofence_enabled === false) {
    return {
      status: GEOFENCE_STATUS.SITE_GEOFENCE_DISABLED,
      message: `Geo-fencing is disabled for ${siteConfig.name || "this site"}.`,
      distance: null,
      allowed: true,
    };
  }

  const siteLat = siteConfig.latitude;
  const siteLon = siteConfig.longitude;
  const radius = Number(siteConfig.geofence_radius || 100);

  if (
    siteLat === null ||
    siteLat === undefined ||
    siteLon === null ||
    siteLon === undefined ||
    String(siteLat).trim() === "" ||
    String(siteLon).trim() === "" ||
    isNaN(Number(siteLat)) ||
    isNaN(Number(siteLon)) ||
    radius <= 0
  ) {
    return {
      status: GEOFENCE_STATUS.SITE_GEOFENCE_NOT_CONFIGURED,
      message: "Site location is not configured. Please contact the administrator.",
      distance: null,
      allowed: false,
    };
  }

  const distance = calculateHaversineDistance(userLat, userLon, siteLat, siteLon);
  if (distance === null) {
    return {
      status: GEOFENCE_STATUS.LOCATION_UNAVAILABLE,
      message: "Unable to calculate distance from site.",
      distance: null,
      allowed: false,
    };
  }

  const numAccuracy = Number(accuracy) || 0;

  // 100-meter rule: Allow submission when user distance <= radius (default 100m)
  if (distance <= radius) {
    return {
      status: GEOFENCE_STATUS.WITHIN_SITE,
      message: `Within allowed site area (${Math.round(distance)}m from site).`,
      distance: Math.round(distance),
      accuracy: numAccuracy,
      radius,
      allowed: true,
    };
  }

  return {
    status: GEOFENCE_STATUS.OUTSIDE_SITE,
    message: `You are outside the allowed ${radius}-meter site area. You are ${Math.round(distance)}m away from the site. Move within ${radius}m to submit.`,
    distance: Math.round(distance),
    accuracy: numAccuracy,
    radius,
    allowed: false,
  };
}

/**
 * Backend API Client Methods
 */

export async function fetchSiteGeoFence(siteId) {
  let cached = null;
  try {
    const raw = localStorage.getItem(`SITE_GEOFENCE_${siteId}`);
    if (raw) cached = JSON.parse(raw);
  } catch {}

  try {
    const res = await projectInstance.get(`/projects/${siteId}/geofence/`);
    if (res?.data) {
      const data = res.data?.data || res.data;
      // If geofence is disabled, ensure coordinates are completely nullified
      if (data.geofence_enabled === false) {
        data.latitude = null;
        data.longitude = null;
        data.is_geofence_configured = false;
        data.status = "GEOFENCE_DISABLED";
        try {
          localStorage.setItem(`SITE_GEOFENCE_${siteId}`, JSON.stringify(data));
        } catch {}
        return data;
      }

      // If remote backend returns unconfigured (latitude is null or empty) but admin configured locally in cache:
      if ((!data.latitude || !data.longitude) && cached?.latitude && cached?.longitude && cached?.geofence_enabled !== false) {
        return {
          ...data,
          ...cached,
          is_geofence_configured: true,
          status: "GEOFENCE_ENABLED",
        };
      }
      try {
        localStorage.setItem(`SITE_GEOFENCE_${siteId}`, JSON.stringify(data));
      } catch {}
      return data;
    }
  } catch (err) {
    if (cached) {
      if (cached.geofence_enabled === false) {
        return {
          ...cached,
          latitude: null,
          longitude: null,
          is_geofence_configured: false,
          status: "GEOFENCE_DISABLED",
        };
      }
      return cached;
    }
    throw err;
  }

  if (cached) {
    if (cached.geofence_enabled === false) {
      return {
        ...cached,
        latitude: null,
        longitude: null,
        is_geofence_configured: false,
        status: "GEOFENCE_DISABLED",
      };
    }
    return cached;
  }
}

export async function saveSiteGeoFence(siteId, data) {
  const isEnabled = data.geofence_enabled !== false;
  const cachedObj = {
    id: Number(siteId) || siteId,
    ...data,
    geofence_enabled: isEnabled,
    latitude: isEnabled ? data.latitude : null,
    longitude: isEnabled ? data.longitude : null,
    is_geofence_configured: isEnabled && !!(data.latitude && data.longitude),
    status: isEnabled ? (data.latitude && data.longitude ? "GEOFENCE_ENABLED" : "LOCATION_NOT_CONFIGURED") : "GEOFENCE_DISABLED",
    updated_at: new Date().toISOString(),
  };

  try {
    localStorage.setItem(`SITE_GEOFENCE_${siteId}`, JSON.stringify(cachedObj));
  } catch {}

  try {
    const res = await projectInstance.put(`/projects/${siteId}/geofence/`, {
      ...data,
      latitude: isEnabled ? data.latitude : null,
      longitude: isEnabled ? data.longitude : null,
      geofence_enabled: isEnabled,
    });
    if (res?.data) {
      const respData = res.data?.data || res.data;
      if (!isEnabled) {
        respData.latitude = null;
        respData.longitude = null;
        respData.is_geofence_configured = false;
        respData.status = "GEOFENCE_DISABLED";
      }
      try {
        localStorage.setItem(`SITE_GEOFENCE_${siteId}`, JSON.stringify(respData));
      } catch {}
      return respData;
    }
    return cachedObj;
  } catch (err) {
    const isDeploymentPending =
      err?.response?.status === 403 ||
      err?.response?.status === 404 ||
      !err?.response;

    if (isDeploymentPending) {
      return cachedObj;
    }
    throw err;
  }
}

export async function fetchGeoFenceOverview(orgId) {
  try {
    const url = orgId ? `/projects/geofence-overview/?org_id=${orgId}` : `/projects/geofence-overview/`;
    const res = await projectInstance.get(url);
    if (Array.isArray(res?.data)) {
      return res.data.map(site => {
        if (site.geofence_enabled === false) {
          return {
            ...site,
            latitude: null,
            longitude: null,
            is_geofence_configured: false,
            status: "GEOFENCE_DISABLED",
          };
        }
        try {
          const raw = localStorage.getItem(`SITE_GEOFENCE_${site.id}`);
          if (raw) {
            const cached = JSON.parse(raw);
            if (cached?.geofence_enabled === false) {
              return {
                ...site,
                latitude: null,
                longitude: null,
                is_geofence_configured: false,
                status: "GEOFENCE_DISABLED",
              };
            }
            if (cached?.latitude && cached?.longitude) {
              return {
                ...site,
                ...cached,
                is_geofence_configured: true,
                status: "GEOFENCE_ENABLED",
              };
            }
          }
        } catch {}
        return site;
      });
    }
  } catch (err) {
    // Retrieve cached items from localStorage
    const cachedList = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("SITE_GEOFENCE_")) {
          const item = JSON.parse(localStorage.getItem(key));
          if (item && (!orgId || Number(item.organization_id) === Number(orgId))) {
            if (item.geofence_enabled === false) {
              item.latitude = null;
              item.longitude = null;
              item.is_geofence_configured = false;
              item.status = "GEOFENCE_DISABLED";
            }
            cachedList.push(item);
          }
        }
      }
    } catch {}
    return cachedList;
  }
  return [];
}

export async function validateSiteLocationWithBackend(siteId, { latitude, longitude, accuracy }) {
  try {
    const res = await projectInstance.post(`/projects/${siteId}/validate-location/`, {
      latitude,
      longitude,
      accuracy,
    });
    return res.data;
  } catch (err) {
    // If remote 404s, perform client-side fallback evaluation using cached site geofence
    try {
      const cached = localStorage.getItem(`SITE_GEOFENCE_${siteId}`);
      if (cached) {
        const site = JSON.parse(cached);
        return evaluateLocationStatus({
          userLat: latitude,
          userLon: longitude,
          accuracy,
          siteConfig: site,
        });
      }
    } catch {}
    throw err;
  }
}

/**
 * Checks if a project currently has an active, enabled, and configured geo-fence.
 */
export async function isSiteGeoFenceActive(projectId) {
  if (!projectId) return false;
  try {
    const config = await fetchSiteGeoFence(projectId);
    const isEnabled = config?.geofence_enabled !== false;
    const hasCoords = Boolean(
      config?.latitude !== null &&
      config?.latitude !== undefined &&
      config?.latitude !== "" &&
      config?.longitude !== null &&
      config?.longitude !== undefined &&
      config?.longitude !== ""
    );
    return Boolean(isEnabled && config?.is_geofence_configured && hasCoords);
  } catch {
    return false;
  }
}

/**
 * Centralized Geo-Fence Presence Verification Gating.
 * 
 * Flow:
 * 1. Checks site geofence configuration FIRST before calling any location API.
 * 2. If geofence is disabled OR not configured:
 *    - Immediately returns { allowed: true, isBypassed: true, coords: null }.
 *    - NEVER calls navigator.geolocation or prompts the browser.
 *    - NEVER calls geofence validation API endpoints.
 * 3. If geofence is enabled AND configured:
 *    - Requests high-accuracy GPS coordinates via getCurrentGPSLocation().
 *    - Evaluates geofence boundary (client + server).
 *    - Returns { allowed: bool, isBypassed: false, coords, status, message, distance, accuracy }.
 */
export async function verifySitePresence({ projectId, siteConfig: providedConfig = null, radius = 100, customOptions = {} } = {}) {
  if (!projectId && !providedConfig) {
    return {
      allowed: true,
      status: GEOFENCE_STATUS.SITE_GEOFENCE_NOT_CONFIGURED,
      message: "No project specified; geo-fencing bypassed.",
      coords: null,
      latitude: null,
      longitude: null,
      distance: null,
      isBypassed: true,
    };
  }

  // Step 1: Check site configuration FIRST
  let siteConfig = providedConfig;
  if (!siteConfig && projectId) {
    try {
      siteConfig = await fetchSiteGeoFence(projectId);
    } catch (err) {
      console.warn("Unable to fetch site geofence configuration:", err);
    }
  }

  const isEnabled = siteConfig?.geofence_enabled !== false;
  const hasCoords = Boolean(
    siteConfig &&
    siteConfig.latitude !== null &&
    siteConfig.latitude !== undefined &&
    siteConfig.latitude !== "" &&
    siteConfig.longitude !== null &&
    siteConfig.longitude !== undefined &&
    siteConfig.longitude !== "" &&
    !isNaN(Number(siteConfig.latitude)) &&
    !isNaN(Number(siteConfig.longitude))
  );
  const isConfigured = Boolean(siteConfig?.is_geofence_configured && hasCoords);

  // If geofence is disabled or unconfigured, completely bypass location check
  if (!isEnabled || !isConfigured) {
    return {
      allowed: true,
      status: !isEnabled ? GEOFENCE_STATUS.SITE_GEOFENCE_DISABLED : GEOFENCE_STATUS.SITE_GEOFENCE_NOT_CONFIGURED,
      message: !isEnabled
        ? `Geo-fencing is disabled for ${siteConfig?.name || "this project"}.`
        : `Site location is not configured. Please contact the administrator.`,
      coords: null,
      latitude: null,
      longitude: null,
      distance: null,
      isBypassed: true,
      siteConfig,
    };
  }

  // Step 2: Only if Geo-Fence is enabled & configured, request browser GPS
  let coords = null;
  try {
    coords = await getCurrentGPSLocation(customOptions);
  } catch (geoErr) {
    return {
      allowed: false,
      status: geoErr.status || GEOFENCE_STATUS.LOCATION_UNAVAILABLE,
      message: geoErr.userMessage || "Location permission is required to submit.",
      coords: null,
      latitude: null,
      longitude: null,
      distance: null,
      accuracy: null,
      isBypassed: false,
      siteConfig,
    };
  }

  // Step 3: Local evaluation for instant perimeter check (100-meter rule)
  const evalConfig = {
    ...siteConfig,
    geofence_radius: siteConfig.geofence_radius || radius || 100,
  };

  const localEval = evaluateLocationStatus({
    userLat: coords.latitude,
    userLon: coords.longitude,
    accuracy: coords.accuracy,
    siteConfig: evalConfig,
  });

  if (!localEval.allowed) {
    return {
      allowed: false,
      status: localEval.status,
      message: localEval.message,
      coords,
      latitude: coords.latitude,
      longitude: coords.longitude,
      distance: localEval.distance,
      accuracy: coords.accuracy,
      isBypassed: false,
      siteConfig,
    };
  }

  // Step 4: Server-side validation
  try {
    const backendResult = await validateSiteLocationWithBackend(projectId, {
      latitude: coords.latitude,
      longitude: coords.longitude,
      accuracy: coords.accuracy,
    });

    const finalDistance = backendResult.distance !== undefined ? backendResult.distance : localEval.distance;

    return {
      allowed: backendResult.allowed,
      status: backendResult.status,
      message: backendResult.message || localEval.message,
      coords,
      latitude: coords.latitude,
      longitude: coords.longitude,
      distance: finalDistance,
      accuracy: coords.accuracy,
      isBypassed: false,
      siteConfig,
    };
  } catch (backendErr) {
    return {
      allowed: localEval.allowed,
      status: localEval.status,
      message: localEval.message,
      coords,
      latitude: coords.latitude,
      longitude: coords.longitude,
      distance: localEval.distance,
      accuracy: coords.accuracy,
      isBypassed: false,
      siteConfig,
    };
  }
}
